import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { API_TAGS } from '@/shared/api';
import { createTerm, deleteTerm, fetchTerm, fetchTerms, TermUpdate, updateTerm } from './api';
import type { TermCreate, TermListParams } from './types';

/**
 * Query keys for terms
 */
export const termKeys = {
  all: [API_TAGS.TERMS] as const,
  lists: () => [...termKeys.all, 'list'] as const,
  list: (params?: TermListParams) => [...termKeys.lists(), { ...params }] as const,
  details: () => [...termKeys.all, 'detail'] as const,
  detail: (id: string | number) => [...termKeys.details(), id] as const,
};

/**
 * Hook to fetch a list of terms
 */
export const useTerms = (params?: TermListParams) => {
  return useQuery({
    queryKey: termKeys.list(params),
    queryFn: () => fetchTerms(params),
    placeholderData: keepPreviousData,
  });
};

/**
 * Hook to fetch terms with infinite scroll (reverse scroll - loads previous pages when scrolling up)
 */
export const useInfiniteTerms = (params?: TermListParams & { limit?: number; offset?: number }) => {
  const limit = params?.limit || 50;
  const initialPageParam = Math.floor((params?.offset || 0) / limit);

  const query = useInfiniteQuery({
    queryKey: termKeys.list(params),
    queryFn: ({ pageParam }) => fetchTerms({ ...params, limit, offset: pageParam * limit }),
    initialPageParam,
    placeholderData: keepPreviousData,
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      // For reverse scroll, we load previous pages (higher offset)
      const totalPages = Math.ceil((lastPage.total || 0) / limit);
      const nextPage = lastPageParam + 1;
      return nextPage < totalPages ? nextPage : undefined;
    },
  });

  const data = useMemo(
    () => ({ total: query.data?.pages[0]?.total, terms: query.data?.pages.flatMap((p) => p.terms) }),
    [query.data],
  );

  return {
    ...query,
    data,
  };
};

/**
 * Hook to fetch a single term by ID
 * @param id - Term ID
 */
export const useTerm = (id?: number | string) => {
  return useQuery({
    queryKey: termKeys.detail(id!),
    queryFn: () => fetchTerm(id!),
    enabled: !!id,
  });
};

/**
 * Hook to create a term
 */
export const useCreateTerm = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: TermCreate) => createTerm(payload),
    onSuccess: (response) => {
      // Set the new term in detail cache (convert TermCreateResponse to TermResponse format)
      queryClient.setQueryData(termKeys.detail(response.id), {
        id: response.id,
        name: response.name,
        description: response.description,
        predicate: response.predicate,
      });
    },
  });
};

/**
 * Hook to update a term
 */
export const useUpdateTerm = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }: { id: number | string; payload: TermUpdate }) => updateTerm(id, payload),
    onSuccess: (updatedTerm) => {
      // Update detail cache
      queryClient.setQueryData(termKeys.detail(updatedTerm.id), updatedTerm);
    },
  });
};

/**
 * Hook to delete a term
 */
export const useDeleteTerm = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number | string) => deleteTerm(id),
    onSuccess: (_, deletedId) => {
      // Invalidate list queries to refetch with updated data

      // Remove from detail cache
      queryClient.removeQueries({ queryKey: termKeys.detail(deletedId) });
    },
  });
};
